// src/pages/AdminMentorsPage.js
import { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Form, Modal, Spinner, Table,
} from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";

const EMPTY_FORM = { username: "", full_name: "", email: "", password: "", confirm_password: "" };

export default function AdminMentorsPage() {
  const { user: currentUser } = useContext(AuthContext);

  const [mentors, setMentors]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [actionError, setActionError] = useState("");

  const [modal, setModal]           = useState(null); // "add" | "edit"
  const [target, setTarget]         = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [toggling, setToggling]     = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/admin/mentors");
      setMentors(res.data.items);
    } catch {
      setError("Failed to load mentors.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function field(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  function openAdd() {
    setForm(EMPTY_FORM);
    setActionError("");
    setTarget(null);
    setModal("add");
  }

  function openEdit(mentor) {
    setForm({
      username: mentor.username ?? "",
      full_name: mentor.full_name,
      email: mentor.email ?? "",
      password: "",
      confirm_password: "",
    });
    setActionError("");
    setTarget(mentor);
    setModal("edit");
  }

  function closeModal() {
    setModal(null);
    setActionError("");
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.username.trim() || !form.full_name.trim() || !form.password) {
      setActionError("Username, full name, and password are required.");
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      await api.post("/admin/mentors", {
        username: form.username.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to create mentor.");
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(e) {
    e.preventDefault();
    if (!form.username.trim() || !form.full_name.trim()) {
      setActionError("Username and full name are required.");
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      await api.put(`/admin/mentors/${target.id}`, {
        username: form.username.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to update mentor.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(mentor) {
    setToggling(mentor.id);
    try {
      await api.put(`/admin/mentors/${mentor.id}`, { is_active: !mentor.is_active });
      await load();
    } catch {
      setError("Failed to update mentor status.");
    } finally {
      setToggling(null);
    }
  }

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex align-items-center mb-4 gap-3">
        <Link to="/admin" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1" />Back
        </Link>
        <h3 className="mb-0">
          <i className="bi bi-person-heart me-2" />
          Mentors
        </h3>
        <Button size="sm" className="ms-auto" onClick={openAdd}>
          <i className="bi bi-plus-lg me-1" />Add Mentor
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {mentors.length === 0 && !error ? (
        <p className="text-muted">No mentors have been added yet.</p>
      ) : (
        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {mentors.map((m) => {
              const isSelf = m.id === currentUser?.id;
              const isBusy = toggling === m.id;
              return (
                <tr key={m.id} className={!m.is_active ? "table-secondary" : ""}>
                  <td className="align-middle fw-semibold">{m.username}</td>
                  <td className="align-middle">{m.full_name}</td>
                  <td className="align-middle text-muted">{m.email || <span className="fst-italic">—</span>}</td>
                  <td className="align-middle">
                    <div className="d-flex gap-1 flex-wrap">
                      {m.is_active
                        ? <Badge bg="success">Active</Badge>
                        : <Badge bg="secondary">Disabled</Badge>}
                      {m.must_change_password && (
                        <Badge bg="warning" text="dark">Pwd Reset</Badge>
                      )}
                    </div>
                  </td>
                  <td className="align-middle">
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(m)}>
                        Edit
                      </Button>
                      {!isSelf && (
                        <Button
                          size="sm"
                          variant={m.is_active ? "outline-danger" : "outline-success"}
                          disabled={isBusy}
                          onClick={() => handleToggleActive(m)}
                        >
                          {isBusy
                            ? <Spinner animation="border" size="sm" />
                            : m.is_active ? "Disable" : "Enable"}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Add Mentor Modal */}
      <Modal show={modal === "add"} onHide={closeModal} centered>
        <Form onSubmit={handleAdd}>
          <Modal.Header closeButton>
            <Modal.Title>Add Mentor</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Username <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.username} onChange={field("username")} placeholder="jsmith" autoFocus />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.full_name} onChange={field("full_name")} placeholder="Jane Smith" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={field("email")} placeholder="jane@example.com" />
            </Form.Group>
            <Form.Group>
              <Form.Label>Password <span className="text-danger">*</span></Form.Label>
              <Form.Control type="password" value={form.password} onChange={field("password")} />
              <Form.Text className="text-muted">Mentor will be prompted to change this on first login.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Create Mentor"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Mentor Modal */}
      <Modal show={modal === "edit"} onHide={closeModal} centered>
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Mentor</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Username <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.username} onChange={field("username")} autoFocus />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.full_name} onChange={field("full_name")} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={field("email")} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
